<template>
  <div class="d-flex align-center w-100">
    <div
      v-for="menuItem in sortedMainMenus"
      :key="menuItem.id"
      class="menu-item"
    >
      <!-- ===================================================== -->
      <!-- DROPDOWN MAIN MENU -->
      <!-- ===================================================== -->
      <!--
        hasDropDown=true always wins.

        Even if isLink=true, this top-level item is treated
        only as a dropdown activator and will not navigate.
      -->
      <v-menu
        v-if="toBoolean(menuItem.hasDropDown)"
        v-model="openMenus[menuItem.id]"
        open-on-hover
        :offset="4"
        eager
        :open-delay="150"
        :close-delay="300"
        transition="scale-transition"
        content-class="mega-menu"
        width="100%"
      >
        <!-- =================================================== -->
        <!-- DROPDOWN ACTIVATOR -->
        <!-- =================================================== -->
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            class="menu-btn text-none"
            :class="{
              'menu-active':
                openMenus[menuItem.id]
            }"
            :style="{
              color: theme.appBarColor,
              fontSize: '16px'
            }"
          >
            <!-- PREPEND ICON -->
            <v-icon
              v-if="
                toBoolean(menuItem.showIcon) &&
                menuItem.mdiIcon
              "
              start
              :icon="menuItem.mdiIcon"
              size="20"
            />

            <!-- LABEL -->
            {{
              getLocalizedText(
                menuItem,
                "desc",
                "descFR"
              )
            }}

            <!-- DROPDOWN INDICATOR -->
            <v-icon
              end
              size="20"
            >
              mdi-menu-down
            </v-icon>
          </v-btn>
        </template>

        <!-- =================================================== -->
        <!-- MEGA MENU CONTENT -->
        <!-- =================================================== -->
        <v-container
          fluid
          class="pa-0 mega-menu-container"
          :style="{
            background:
              theme.appBarBg,
            color:
              theme.appBarColor
          }"
        >
          <v-row
            no-gutters
            class="mega-menu-row"
          >
            <v-col
              v-for="col in getColumnsForMain(menuItem.id)"
              :key="col.colId"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class="mega-column"
            >
              <!-- ============================================= -->
              <!-- COLUMN IMAGE -->
              <!-- ============================================= -->
              <div
                v-if="
                  toBoolean(col.showColImage) &&
                  col.colImage
                "
                class="column-image-wrapper"
              >
                <img
                  :src="col.colImage"
                  :alt="
                    getLocalizedText(
                      col,
                      'desc',
                      'descFR'
                    )
                  "
                  class="column-image"
                />
              </div>

              <!-- ============================================= -->
              <!-- NORMAL COLUMN -->
              <!-- ============================================= -->
              <div v-else>
                <!-- COLUMN HEADER -->
                <div class="column-heading-row">
                  <v-icon
                    v-if="col.mdiIcon"
                    :icon="col.mdiIcon"
                    size="24"
                  />

                  <h4 class="column-heading">
                    {{
                      getLocalizedText(
                        col,
                        "desc",
                        "descFR"
                      )
                    }}
                  </h4>
                </div>

                <!-- =========================================== -->
                <!-- COLUMN ITEMS -->
                <!-- =========================================== -->
                <template
                  v-for="item in getItemsForColumn(col.colId)"
                  :key="item.id"
                >
                  <!-- CLICKABLE COLUMN ITEM -->
                  <component
                    :is="
                      getMenuHref(item)
                        ? 'a'
                        : 'router-link'
                    "
                    v-if="
                      isMenuItemClickable(
                        item
                      )
                    "
                    :to="
                      getMenuRoute(
                        item
                      )
                    "
                    :href="
                      getMenuHref(
                        item
                      )
                    "
                    :target="
                      getMenuHref(item)
                        ? '_blank'
                        : undefined
                    "
                    :rel="
                      getMenuHref(item)
                        ? 'noopener noreferrer'
                        : undefined
                    "
                    class="
                      menu-link
                      no-link-style
                    "
                    :style="{
                      color:
                        theme.appBarColor
                    }"
                    @click="
                      closeMainMenu(
                        menuItem.id
                      )
                    "
                  >
                    <v-icon
                      v-if="item.mdiIcon"
                      :icon="item.mdiIcon"
                      size="24"
                      class="menu-link-icon"
                    />

                    <div class="menu-link-content">
                      <div class="menu-main-description">
                        {{
                          getLocalizedText(
                            item,
                            "mainDesc",
                            "mainDescFR"
                          )
                        }}
                      </div>

                      <div
                        v-if="
                          getLocalizedText(
                            item,
                            'subDesc',
                            'subDescFR'
                          )
                        "
                        class="menu-sub-description"
                      >
                        {{
                          getLocalizedText(
                            item,
                            "subDesc",
                            "subDescFR"
                          )
                        }}
                      </div>
                    </div>
                  </component>

                  <!-- NON-CLICKABLE COLUMN ITEM -->
                  <div
                    v-else
                    class="
                      menu-link
                      disabled-menu-link
                    "
                    :style="{
                      color:
                        theme.appBarColor
                    }"
                  >
                    <v-icon
                      v-if="item.mdiIcon"
                      :icon="item.mdiIcon"
                      size="24"
                      class="menu-link-icon"
                    />

                    <div class="menu-link-content">
                      <div class="menu-main-description">
                        {{
                          getLocalizedText(
                            item,
                            "mainDesc",
                            "mainDescFR"
                          )
                        }}
                      </div>

                      <div
                        v-if="
                          getLocalizedText(
                            item,
                            'subDesc',
                            'subDescFR'
                          )
                        "
                        class="menu-sub-description"
                      >
                        {{
                          getLocalizedText(
                            item,
                            "subDesc",
                            "subDescFR"
                          )
                        }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- EMPTY COLUMN -->
                <div
                  v-if="
                    getItemsForColumn(
                      col.colId
                    ).length === 0
                  "
                  class="empty-column"
                >
                  {{
                    currentLanguage === "fr"
                      ? "Aucun élément disponible"
                      : "No items available"
                  }}
                </div>
              </div>
            </v-col>

            <!-- =============================================== -->
            <!-- NO COLUMNS -->
            <!-- =============================================== -->
            <v-col
              v-if="
                getColumnsForMain(
                  menuItem.id
                ).length === 0
              "
              cols="12"
            >
              <div class="empty-main-menu">
                {{
                  currentLanguage === "fr"
                    ? "Aucun contenu disponible"
                    : "No menu content available"
                }}
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-menu>

      <!-- ===================================================== -->
      <!-- DIRECT MAIN MENU LINK -->
      <!-- ===================================================== -->
      <!--
        Navigates only when:

        hasDropDown = false
        isLink      = true
        route/href  = valid
      -->
      <component
        :is="
          getMenuHref(menuItem)
            ? 'a'
            : 'router-link'
        "
        v-else-if="
          isMainMenuNavigatable(
            menuItem
          )
        "
        :to="
          getMenuRoute(
            menuItem
          )
        "
        :href="
          getMenuHref(
            menuItem
          )
        "
        :target="
          getMenuHref(menuItem)
            ? '_blank'
            : undefined
        "
        :rel="
          getMenuHref(menuItem)
            ? 'noopener noreferrer'
            : undefined
        "
        class="
          main-menu-link
          no-link-style
        "
        :style="{
          color:
            theme.appBarColor
        }"
      >
        <v-btn
          variant="text"
          class="menu-btn text-none"
          :style="{
            color:
              theme.appBarColor,
            fontSize:
              '16px'
          }"
        >
          <!-- PREPEND ICON -->
          <v-icon
            v-if="
              toBoolean(menuItem.showIcon) &&
              menuItem.mdiIcon
            "
            start
            :icon="menuItem.mdiIcon"
            size="20"
          />

          {{
            getLocalizedText(
              menuItem,
              "desc",
              "descFR"
            )
          }}
        </v-btn>
      </component>

      <!-- ===================================================== -->
      <!-- NON-LINK / NON-DROPDOWN LABEL -->
      <!-- ===================================================== -->
      <v-btn
        v-else
        variant="text"
        class="
          menu-btn
          text-none
          main-menu-disabled
        "
        :style="{
          color:
            theme.appBarColor,
          fontSize:
            '16px'
        }"
      >
        <v-icon
          v-if="
            toBoolean(menuItem.showIcon) &&
            menuItem.mdiIcon
          "
          start
          :icon="menuItem.mdiIcon"
          size="20"
        />

        {{
          getLocalizedText(
            menuItem,
            "desc",
            "descFR"
          )
        }}
      </v-btn>
    </div>
  </div>

  <v-spacer />

  <!-- ======================================================= -->
  <!-- ERROR -->
  <!-- ======================================================= -->
  <v-snackbar
    :model-value="
      Boolean(loadError)
    "
    color="error"
    timeout="8000"
  >
    {{ loadError }}
  </v-snackbar>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  watch
} from "vue";

import {
  useI18n
} from "vue-i18n";

import publicApi
  from "@/auth/publicApi";

import {
  useLangStore
} from "@/store/lang";

import {
  useThemeStore
} from "@/store/auth";

// ============================================================
// STORES
// ============================================================

const lang =
  useLangStore();

const themeStore =
  useThemeStore();

const {
  locale
} = useI18n();

// ============================================================
// DATA
// ============================================================

const megaMainMenu =
  ref([]);

const megaColumn =
  ref([]);

const megaColumnItem =
  ref([]);

const megaColumnImage =
  ref([]);

// ============================================================
// STATE
// ============================================================

const loading =
  ref(false);

const loadError =
  ref("");

const openMenus =
  ref({});

// ============================================================
// LANGUAGE
// ============================================================

const currentLanguage =
  computed(
    () =>
      lang.currentLanguage ||
      "en"
  );

watch(
  currentLanguage,
  newLanguage => {
    locale.value =
      newLanguage ||
      "en";
  },
  {
    immediate: true
  }
);

// ============================================================
// THEME
// ============================================================

const theme =
  computed(
    () =>
      themeStore.themeStyles ||
      {
        appBarBg:
          "#ffffff",

        appBarColor:
          "#000000"
      }
  );

// ============================================================
// NORMALIZE ARRAY
// ============================================================

function normalizeArray(
  data
) {
  if (
    Array.isArray(data)
  ) {
    return data;
  }

  if (
    Array.isArray(
      data?.data
    )
  ) {
    return data.data;
  }

  if (
    Array.isArray(
      data?.items
    )
  ) {
    return data.items;
  }

  if (
    Array.isArray(
      data?.results
    )
  ) {
    return data.results;
  }

  if (
    Array.isArray(
      data?.value
    )
  ) {
    return data.value;
  }

  return [];
}

// ============================================================
// BOOLEAN NORMALIZATION
// ============================================================

function toBoolean(
  value
) {
  if (
    typeof value ===
    "boolean"
  ) {
    return value;
  }

  if (
    typeof value ===
    "number"
  ) {
    return value === 1;
  }

  if (
    typeof value ===
    "string"
  ) {
    return [
      "true",
      "1",
      "yes",
      "on"
    ].includes(
      value
        .trim()
        .toLowerCase()
    );
  }

  return false;
}

// ============================================================
// ID COMPARISON
// ============================================================

function sameId(
  firstId,
  secondId
) {
  if (
    firstId == null ||
    secondId == null
  ) {
    return false;
  }

  return (
    String(firstId)
      .trim() ===
    String(secondId)
      .trim()
  );
}

// ============================================================
// MAIN MENU SORTING
// ============================================================

const sortedMainMenus =
  computed(
    () =>
      [...megaMainMenu.value]
        .sort(
          (
            first,
            second
          ) =>
            Number(
              first?.colOrder ||
              0
            ) -
            Number(
              second?.colOrder ||
              0
            )
        )
  );

// ============================================================
// COLUMNS
// ============================================================

function getColumnsForMain(
  mainMenuId
) {
  return megaColumn.value
    .filter(
      column =>
        sameId(
          column.mainMenuId,
          mainMenuId
        )
    )
    .sort(
      (
        first,
        second
      ) =>
        Number(
          first?.colOrder ||
          0
        ) -
        Number(
          second?.colOrder ||
          0
        )
    );
}

// ============================================================
// COLUMN ITEMS
// ============================================================

function getItemsForColumn(
  columnId
) {
  return megaColumnItem.value
    .filter(
      item =>
        sameId(
          item.colId,
          columnId
        )
    )
    .sort(
      (
        first,
        second
      ) =>
        Number(
          first?.menuorder ||
          0
        ) -
        Number(
          second?.menuorder ||
          0
        )
    );
}

// ============================================================
// LOCALIZED TEXT
// ============================================================

function getLocalizedText(
  record,
  englishProperty,
  frenchProperty
) {
  if (!record) {
    return "";
  }

  if (
    currentLanguage.value ===
    "fr"
  ) {
    return (
      record[frenchProperty] ||
      record[englishProperty] ||
      ""
    );
  }

  return (
    record[englishProperty] ||
    record[frenchProperty] ||
    ""
  );
}

// ============================================================
// VALUE CLEANING
// ============================================================

function cleanValue(
  value
) {
  const normalized =
    String(
      value ??
      ""
    )
      .trim();

  if (
    !normalized ||
    normalized === "#" ||
    normalized
      .toLowerCase() ===
      "null" ||
    normalized
      .toLowerCase() ===
      "undefined"
  ) {
    return "";
  }

  return normalized;
}

// ============================================================
// EXTERNAL URL
// ============================================================

function isExternalUrl(
  value
) {
  return /^(https?:)?\/\//i
    .test(
      cleanValue(
        value
      )
    );
}

// ============================================================
// DYNAMIC PAGE SLUG
// ============================================================

function getDynamicPageSlug(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.dynamicPageSlugFr ||
      item?.dynamicPageSlugEn
    );
  }

  return cleanValue(
    item?.dynamicPageSlugEn ||
    item?.dynamicPageSlugFr
  );
}

// ============================================================
// STATIC ROUTE VALUE
// ============================================================

function getStaticRouteValue(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.menuPathFr ||
      item?.menuPathEn ||
      item?.linkPathFr ||
      item?.linkPath
    );
  }

  return cleanValue(
    item?.menuPathEn ||
    item?.menuPathFr ||
    item?.linkPath ||
    item?.linkPathFr
  );
}

// ============================================================
// EXTERNAL LINK
// ============================================================

function getExternalLink(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.linkPathFr ||
      item?.linkPath
    );
  }

  return cleanValue(
    item?.linkPath ||
    item?.linkPathFr
  );
}

// ============================================================
// ROUTER DESTINATION
// ============================================================

function getMenuRoute(
  item
) {
  if (!item) {
    return undefined;
  }

  // ----------------------------------------------------------
  // DYNAMIC PAGE
  // ----------------------------------------------------------

  if (
    item.dynamicPageId
  ) {
    if (
      item.dynamicPageIsPublished ===
      false
    ) {
      return undefined;
    }

    const slug =
      getDynamicPageSlug(
        item
      );

    if (!slug) {
      return undefined;
    }

    return {
      name:
        "DynamicPublicPage",

      params: {
        lang:
          currentLanguage.value,

        pagePath:
          slug
            .split("/")
            .map(
              segment =>
                segment.trim()
            )
            .filter(
              Boolean
            )
      }
    };
  }

  // ----------------------------------------------------------
  // STATIC ROUTE
  // ----------------------------------------------------------

  const routeValue =
    getStaticRouteValue(
      item
    );

  if (
    !routeValue ||
    isExternalUrl(
      routeValue
    )
  ) {
    return undefined;
  }

  // Path
  if (
    routeValue.startsWith(
      "/"
    )
  ) {
    return {
      path:
        routeValue
    };
  }

  // Named Vue route
  return {
    name:
      routeValue
  };
}

// ============================================================
// EXTERNAL HREF
// ============================================================

function getMenuHref(
  item
) {
  if (
    !item ||
    item.dynamicPageId
  ) {
    return undefined;
  }

  const link =
    getExternalLink(
      item
    );

  return isExternalUrl(
    link
  )
    ? link
    : undefined;
}

// ============================================================
// MEGA MENU ITEM CLICKABILITY
// ============================================================

function isMenuItemClickable(
  item
) {
  if (!item) {
    return false;
  }

  /*
   * Respect IsLink if the backend provides it.
   *
   * Undefined is treated as true for compatibility
   * with existing records created before IsLink.
   */
  if (
    item.isLink !==
      undefined &&
    item.isLink !==
      null &&
    !toBoolean(
      item.isLink
    )
  ) {
    return false;
  }

  return Boolean(
    getMenuRoute(
      item
    ) ||
    getMenuHref(
      item
    )
  );
}

// ============================================================
// TOP-LEVEL MAIN MENU NAVIGATION
// ============================================================

function isMainMenuNavigatable(
  menuItem
) {
  if (!menuItem) {
    return false;
  }

  /*
   * RULE 1
   *
   * hasDropDown=true means this record is
   * exclusively a dropdown parent.
   *
   * It must never navigate directly.
   */
  if (
    toBoolean(
      menuItem.hasDropDown
    )
  ) {
    return false;
  }

  /*
   * RULE 2
   *
   * The MainMenu record must explicitly
   * be configured as a link.
   */
  if (
    !toBoolean(
      menuItem.isLink
    )
  ) {
    return false;
  }

  /*
   * RULE 3
   *
   * A valid route, dynamic page, or external
   * URL must actually exist.
   */
  return Boolean(
    getMenuRoute(
      menuItem
    ) ||
    getMenuHref(
      menuItem
    )
  );
}

// ============================================================
// CLOSE DROPDOWN
// ============================================================

function closeMainMenu(
  menuId
) {
  if (
    menuId == null
  ) {
    return;
  }

  openMenus.value[
    menuId
  ] = false;
}

// ============================================================
// LOAD MEGA MENU
// ============================================================

async function loadMegaMenu() {
  loading.value =
    true;

  loadError.value =
    "";

  try {
    const [
      mainMenuResponse,
      columnResponse,
      itemResponse,
      imageResponse
    ] =
      await Promise.all([
        publicApi.get(
          "/MainMenu"
        ),

        publicApi.get(
          "/MegamenuColumn"
        ),

        publicApi.get(
          "/MegamenuItems"
        ),

        publicApi.get(
          "/MegamenuImages"
        )
      ]);

    megaMainMenu.value =
      normalizeArray(
        mainMenuResponse.data
      );

    megaColumn.value =
      normalizeArray(
        columnResponse.data
      );

    megaColumnItem.value =
      normalizeArray(
        itemResponse.data
      );

    megaColumnImage.value =
      normalizeArray(
        imageResponse.data
      );
  }
  catch (error) {
    console.error(
      "Unable to load mega menu:",
      error
    );

    loadError.value =
      error?.response
        ?.data
        ?.message ||
      error?.response
        ?.data
        ?.detail ||
      error?.message ||
      "Unable to load the menu.";
  }
  finally {
    loading.value =
      false;
  }
}

// ============================================================
// INITIALIZE
// ============================================================

onMounted(
  loadMegaMenu
);
</script>

<style scoped>
/* ============================================================
   TOP LEVEL
============================================================ */

.menu-item {
  display: flex;
  align-items: center;
  margin-right: 4px;
}

/* ============================================================
   TOP LEVEL BUTTON
============================================================ */

.menu-btn {
  position: relative;

  min-width: 0;

  overflow: visible;

  font-weight: 400;
}

.menu-btn
:deep(.v-btn__content) {
  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}

/* ============================================================
   PREPEND ICON
============================================================ */

.menu-btn
:deep(.v-icon--start) {
  margin-inline-end: 7px;
}

/* ============================================================
   MAIN MENU LINK
============================================================ */

.main-menu-link {
  display: inline-flex;

  align-items: center;

  color: inherit;

  text-decoration: none;
}

.main-menu-link:hover {
  color: inherit;

  text-decoration: none;
}

/* ============================================================
   NON-CLICKABLE MAIN MENU
============================================================ */

.main-menu-disabled {
  cursor: default;
}

/* ============================================================
   ACTIVE / HOVER
============================================================ */

.menu-btn:hover,
.menu-active {
  border-bottom:
    2px solid red;
}

.menu-btn:hover::before,
.menu-active::before {
  content: "";

  position: absolute;

  left: 50%;

  bottom: -18px;

  transform:
    translateX(-50%);

  width: 0;

  height: 0;

  border-left:
    16px solid transparent;

  border-right:
    16px solid transparent;

  border-bottom:
    16px solid red;
}

/*
 * Direct links should get the underline,
 * but not necessarily the dropdown triangle.
 */
.main-menu-link
.menu-btn:hover::before {
  display: none;
}

/* ============================================================
   MEGA MENU OVERLAY
============================================================ */

.mega-menu {
  min-width:
    min(
      1100px,
      95vw
    );

  max-width:
    100vw;

  border-radius:
    12px;

  will-change:
    transform,
    opacity;

  transform:
    translateZ(0);
}

/* ============================================================
   MEGA MENU CONTAINER
============================================================ */

.mega-menu-container {
  margin-top:
    16px;

  border-radius:
    8px;

  overflow:
    hidden;
}

/* ============================================================
   ROW
============================================================ */

.mega-menu-row {
  padding:
    30px
    50px
    50px;
}

/* ============================================================
   COLUMN
============================================================ */

.mega-column {
  min-height:
    200px;

  padding:
    20px;
}

/* ============================================================
   COLUMN HEADER
============================================================ */

.column-heading-row {
  display: flex;

  align-items: center;

  gap:
    12px;

  margin-bottom:
    16px;
}

.column-heading {
  margin:
    0;

  font-size:
    18px;

  line-height:
    1.3;
}

/* ============================================================
   COLUMN IMAGE
============================================================ */

.column-image-wrapper {
  width:
    100%;
}

.column-image {
  display:
    block;

  width:
    80%;

  max-width:
    100%;

  height:
    auto;

  object-fit:
    cover;

  border-radius:
    8px;
}

/* ============================================================
   MENU LINK
============================================================ */

.menu-link {
  display:
    flex;

  align-items:
    flex-start;

  gap:
    12px;

  padding:
    10px
    6px;

  margin-bottom:
    6px;

  border-radius:
    6px;

  transition:
    background-color
      0.2s ease,
    transform
      0.2s ease;
}

.menu-link:hover {
  background:
    rgba(
      201,
      166,
      107,
      0.14
    );

  transform:
    translateX(
      3px
    );
}

/* ============================================================
   ITEM ICON
============================================================ */

.menu-link-icon {
  flex:
    0 0 auto;

  margin-top:
    2px;
}

/* ============================================================
   ITEM CONTENT
============================================================ */

.menu-link-content {
  min-width:
    0;
}

.menu-main-description {
  font-size:
    16px;

  line-height:
    1.35;
}

.menu-sub-description {
  margin-top:
    3px;

  font-size:
    12px;

  line-height:
    1.4;

  opacity:
    0.75;
}

/* ============================================================
   LINK RESET
============================================================ */

.no-link-style {
  color:
    inherit;

  text-decoration:
    none;
}

/* ============================================================
   DISABLED ITEM
============================================================ */

.disabled-menu-link {
  cursor:
    default;

  opacity:
    0.65;
}

.disabled-menu-link:hover {
  background:
    transparent;

  transform:
    none;
}

/* ============================================================
   EMPTY STATES
============================================================ */

.empty-column {
  padding:
    8px
    0;

  font-size:
    13px;

  opacity:
    0.65;
}

.empty-main-menu {
  width:
    100%;

  padding:
    35px;

  text-align:
    center;

  font-size:
    13px;

  opacity:
    0.65;
}

/* ============================================================
   RESPONSIVE
============================================================ */

@media (
  max-width: 960px
) {
  .mega-menu-row {
    padding:
      20px;
  }

  .mega-menu {
    min-width:
      90vw;
  }
}
</style>